import { useEffect, useState } from "react";
import { Input, type InputProps } from "./input.tsx";

interface Props {
  /** Initial value when the input mounts. */
  initialValue?: string;
  /** Controlled value. Pair with onValueChange for immediate typing updates. */
  value?: string;
  /** Fires immediately on every keystroke. */
  onValueChange?: (value: string) => void;
  /** Fires after the debounce window. */
  onDebouncedChange: (value: string) => void;
  /** Debounce window in ms. */
  delayMs?: number;
  placeholder?: string;
  className?: string;
  inputSize?: InputProps["inputSize"];
  /** Optional explicit name attribute for autofill scoping. */
  name?: string;
}

/**
 * Standalone search input with built-in debouncing. Uncontrolled by default,
 * with an optional controlled mode for pages that need a clear/reset button.
 */
export function SearchBox({
  initialValue = "",
  value: controlledValue,
  onValueChange,
  onDebouncedChange,
  delayMs = 150,
  placeholder = "Search...",
  className,
  inputSize,
  name,
}: Props) {
  const [localValue, setLocalValue] = useState(initialValue);
  const value = controlledValue ?? localValue;

  useEffect(() => {
    const handle = window.setTimeout(() => onDebouncedChange(value), delayMs);
    return () => window.clearTimeout(handle);
    // onDebouncedChange intentionally not in deps; owners often inline it.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, delayMs]);

  return (
    <Input
      className={className}
      inputSize={inputSize}
      name={name}
      placeholder={placeholder}
      value={value}
      onChange={(e) => {
        const next = e.target.value;
        if (controlledValue === undefined) setLocalValue(next);
        onValueChange?.(next);
      }}
    />
  );
}
